/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    GUEST_ACCESS_PHASE1_ATTRIBUTION
 * Date:         2026-07-05
 * Modified By:  Engineering
 *
 * Description:
 * Captures campaign attribution on landing (BR-19). Reads the email recipient id
 * (?c=) and UTM params from the URL and persists them for the session, mirroring
 * the WordPress `uavzone-visit-tracking` snippet so email + social visitors can be
 * attributed and the funnel closed on signup.
 *
 * Related Files:
 * - server (Phase 3): POST /api/track/visit funnel events
 * - snippets/uavzone-visit-tracking.html (marketing-site counterpart)
 */

const KEY = "uz_attribution";

export interface Attribution {
  recipientId?: string; // ?c= — MailList _id (email path → named contact)
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
}

/**
 * Read ?c= + utm_* from the current URL and persist to sessionStorage.
 * First non-empty capture wins for the session (later UTM-less pages keep it).
 */
export function captureAttribution(): Attribution {
  const existing = getAttribution();
  try {
    const params = new URLSearchParams(window.location.search);
    const fresh: Attribution = {
      recipientId: params.get("c") || undefined,
      utmSource: params.get("utm_source") || undefined,
      utmMedium: params.get("utm_medium") || undefined,
      utmCampaign: params.get("utm_campaign") || undefined,
      utmContent: params.get("utm_content") || undefined,
    };
    const hasFresh = Object.values(fresh).some(Boolean);
    if (!hasFresh) return existing;

    // Merge: keep any previously captured value the new URL doesn't override.
    const merged: Attribution = { ...existing };
    (Object.keys(fresh) as (keyof Attribution)[]).forEach((k) => {
      if (fresh[k]) merged[k] = fresh[k];
    });
    sessionStorage.setItem(KEY, JSON.stringify(merged));
    return merged;
  } catch {
    return existing;
  }
}

export function getAttribution(): Attribution {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attribution) : {};
  } catch {
    return {};
  }
}
